import React from "react";
import { CalendarClock, MessageSquare, StickyNote, Activity as ActIcon, ArrowRight } from "lucide-react";

// Timeline types that are bookkeeping, not meaningful interactions.
const NOISE = new Set(["support_assignment", "recruiter_changed", "status_changed", "task_created", "opportunity_created", "lead", "opportunity", "pipeline_changed"]);
const SIGNIFICANT = new Set(["stage_changed", "candidate_type_changed", "task_completed", "appointment_cancelled", "appointment_rescheduled", "program_enrolled"]);

const fmt = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const fmtT = (v) => (v ? new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export function eventText(e, ctLabel) {
  const d = e.description || e.summary || e.title || "";
  if (d && !/^[a-z0-9_]+$/.test(d)) return ctLabel ? d.replace(/\b[a-z]+(?:_[a-z]+)+\b/g, (k) => { const l = ctLabel(k); return l && l !== k ? l : k.replace(/_/g, " "); }) : d;
  return (e.type || "Activity").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function meetingSplit(meetings) {
  const now = Date.now();
  const active = (meetings || []).filter((m) => !["cancelled", "canceled"].includes(m.status));
  const next = active.filter((m) => m.start && new Date(m.start).getTime() > now).sort((a, b) => new Date(a.start) - new Date(b.start))[0];
  const last = active.filter((m) => m.start && new Date(m.start).getTime() <= now).sort((a, b) => new Date(b.start) - new Date(a.start))[0];
  return { next, last };
}

function Row({ icon: Icon, title, meta, testId }) {
  return (
    <li className="flex items-start gap-2.5" data-testid={testId}>
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0"><p className="text-sm text-slate-700 line-clamp-2">{title}</p><p className="text-[11px] text-slate-400">{meta}</p></div>
    </li>
  );
}

export function RecentInteractions({ notes = [], meetings, timeline = [], onViewAll, ctLabel }) {
  const { next, last } = meetingSplit(meetings);
  const seen = new Set();
  const events = timeline.filter((e) => !NOISE.has(e.type) && (SIGNIFICANT.has(e.type) || !e.type?.startsWith("appointment")))
    .filter((e) => { const k = `${e.type}:${eventText(e)}`; if (seen.has(k)) return false; seen.add(k); return true; });
  const rows = [
    ...(next ? [{ icon: CalendarClock, title: `Next meeting: ${next.title || "Meeting"}`, meta: `${fmtT(next.start)}${next.host_name ? ` · ${next.host_name}` : ""}`, key: "next" }] : []),
    ...(last ? [{ icon: CalendarClock, title: `Last meeting: ${last.title || "Meeting"}${last.outcome_label ? ` · ${last.outcome_label}` : ""}`, meta: `${fmt(last.start)}${last.host_name ? ` · ${last.host_name}` : ""}`, key: "last" }] : []),
    ...notes.slice(0, 2).map((n) => ({ icon: StickyNote, title: n.text || n.body || "Note", meta: `${n.author_name || n.user_name || "Note"} · ${fmt(n.created_at)}`, key: `n${n.id}` })),
    ...events.slice(0, 3).map((e) => ({ icon: e.type === "note_added" ? MessageSquare : ActIcon, title: eventText(e, ctLabel), meta: `${e.user_name || "System"} · ${fmt(e.created_at)}`, key: `e${e.id}` })),
  ].slice(0, 5);
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="recent-interactions">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-sm text-slate-900">Recent Notes, Meetings &amp; Activity</h3>
        {onViewAll && <button onClick={onViewAll} className="text-xs font-medium text-brand hover:underline inline-flex items-center gap-1" data-testid="recent-view-all">Full history <ArrowRight className="w-3 h-3" /></button>}
      </div>
      {rows.length ? <ul className="space-y-3">{rows.map((r) => <Row key={r.key} icon={r.icon} title={r.title} meta={r.meta} testId={`recent-${r.key}`} />)}</ul>
        : <p className="text-sm text-slate-400">No recent notes, meetings or activity.</p>}
    </section>
  );
}
